import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  RadioGroup,
  RadioGroupItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { FormSectionCard } from '../../event-jobs/event-job-create/FormSectionCard';
import type { LinearInputProtocol, LinearOutputType } from '@/api/media/live-linear-jobs-types';
import type { PresetType, ConfigPreset } from '@/api/media/live-event-jobs-types';
import type { CreateLinearJobRequest } from '@/api/media/live-linear-jobs-types';
import { resolveValidOutputs } from './use-linear-job-form';
import styles from './LinearJobCreate.module.css';

const INPUT_PROTOCOLS: { value: LinearInputProtocol; label: string }[] = [
  { value: 'rtmp', label: 'RTMP' },
  { value: 'srt', label: 'SRT' },
  { value: 'srt_unicast', label: 'SRT Unicast' },
  { value: 'mpegts', label: 'MPEG-TS over UDP' },
  { value: 'mpegts_udp_multicast', label: 'MPEG-TS over UDP (Multicast)' },
  { value: 'rtp', label: 'MPEG-TS over RTP (Multicast)' },
  { value: 'mp3rtp', label: 'MP3 over RTP' },
  { value: 'mp3Rtmp', label: 'MP3 over RTMP' },
];

const OUTPUT_OPTIONS: { value: LinearOutputType; label: string }[] = [
  { value: 'rtmp', label: 'RTMP' },
  { value: 'http-flv', label: 'HTTP-FLV' },
  { value: 'hls', label: 'HLS' },
  { value: 'dash', label: 'MPEG-DASH' },
];

const PRESET_LABEL_OVERRIDES: Record<string, string> = { transmux: 'Transmuxing only' };
const EXCLUDED_PRESETS = ['__snapshot__'];

function getDisabledOutputs(inputProtocol: LinearInputProtocol): LinearOutputType[] {
  if (inputProtocol === 'mp3rtp') return ['rtmp', 'http-flv', 'dash'];
  if (inputProtocol === 'mp3Rtmp') return ['rtmp', 'http-flv'];
  if (inputProtocol === 'mpegts' || inputProtocol === 'srt') return ['rtmp', 'http-flv'];
  return [];
}

function toggle<T>(arr: T[], val: T): T[] {
  return arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
}

interface LinearStreamConfigSectionProps {
  form: CreateLinearJobRequest;
  audioOnlyFormat: 'aac' | 'mp3';
  onAudioOnlyFormatChange: (fmt: 'aac' | 'mp3') => void;
  configPresets?: ConfigPreset[];
  errors: Partial<Record<'outputs' | 'presets', string>>;
  onChange: (field: keyof CreateLinearJobRequest, value: unknown) => void;
  clearError: (field: string) => void;
}

/** Input protocol select, output checkboxes, preset checkboxes, audio-only format. */
export function LinearStreamConfigSection({
  form,
  audioOnlyFormat,
  onAudioOnlyFormatChange,
  configPresets,
  errors,
  onChange,
  clearError,
}: LinearStreamConfigSectionProps) {
  const disabledOutputs = getDisabledOutputs(form.input_protocol);

  const handleProtocolChange = (ip: LinearInputProtocol) => {
    onChange('input_protocol', ip);
    clearError('input_protocol');
    const validOuts = resolveValidOutputs(ip, form.outputs);
    if (validOuts.length !== form.outputs.length) {
      onChange('outputs', validOuts);
    }
  };

  const displayPresets: { value: string; label: string }[] = (() => {
    // configPresets undefined = config not yet loaded → show static fallback list
    // configPresets [] (empty from server) → show audio_only only (legacy :139-146)
    if (configPresets === undefined) {
      return [
        { value: '1080p', label: '1080p' },
        { value: '720p', label: '720p' },
        { value: '480p', label: '480p' },
        { value: '360p', label: '360p' },
        { value: 'transmux', label: 'Transmuxing only' },
        { value: 'audio_only', label: 'Audio Only' },
      ];
    }
    const base = configPresets
      .filter((p) => !EXCLUDED_PRESETS.includes(p.value))
      .map((p) => ({ value: p.value, label: PRESET_LABEL_OVERRIDES[p.value] ?? (p.label || p.value) }));
    if (!base.some((p) => p.value === 'audio_only')) {
      base.push({ value: 'audio_only', label: 'Audio Only' });
    }
    return base;
  })();

  return (
    <FormSectionCard title="Stream Configuration" description="Input protocol, output types, and transcoding presets.">
      <div className={styles.grid2}>
        <Field label="Input Protocol" required>
          {(fp) => (
            <Select
              value={form.input_protocol}
              onValueChange={(v) => handleProtocolChange(v as LinearInputProtocol)}
            >
              <SelectTrigger {...fp}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INPUT_PROTOCOLS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <div />
      </div>

      <Field label="Outputs" required error={errors.outputs}>
        {() => (
          <div className={styles.checkGroup}>
            {OUTPUT_OPTIONS.map(({ value, label }) => {
              const blocked = disabledOutputs.includes(value);
              return (
                <label
                  key={value}
                  className={`${styles.checkItem}${blocked ? ` ${styles.checkItemDisabled}` : ''}`}
                  title={blocked ? 'Not available for this input protocol' : undefined}
                >
                  <Checkbox
                    checked={form.outputs.includes(value)}
                    disabled={blocked}
                    aria-disabled={blocked}
                    onCheckedChange={() => {
                      if (blocked) return;
                      onChange('outputs', toggle(form.outputs, value));
                      clearError('outputs');
                    }}
                  />
                  <span>{label}</span>
                </label>
              );
            })}
          </div>
        )}
      </Field>

      <Field label="Transcoding Presets" required error={errors.presets}>
        {() => (
          <div className={styles.checkGroup}>
            {displayPresets.map((p) => (
              <label key={p.value} className={styles.checkItem}>
                <Checkbox
                  checked={form.presets.includes(p.value as PresetType)}
                  onCheckedChange={() => {
                    onChange('presets', toggle(form.presets, p.value as PresetType));
                    clearError('presets');
                  }}
                />
                <span>{p.label}</span>
              </label>
            ))}
          </div>
        )}
      </Field>

      {form.presets.includes('audio_only') && (
        <fieldset className={styles.fieldsetReset}>
          <legend className={styles.fieldLegend}>Audio-Only Format</legend>
          <RadioGroup
            value={audioOnlyFormat}
            onValueChange={(v) => onAudioOnlyFormatChange(v as 'aac' | 'mp3')}
            className={styles.radioGroup}
          >
            {(['aac', 'mp3'] as const).map((fmt) => (
              <label key={fmt} className={styles.radioItem}>
                <RadioGroupItem value={fmt} id={`linear-audio-fmt-${fmt}`} />
                <span>{fmt.toUpperCase()}</span>
              </label>
            ))}
          </RadioGroup>
        </fieldset>
      )}
    </FormSectionCard>
  );
}
